import { useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useI18n } from './i18n';
import { ROUTES } from './routes';
import Schedule from './Schedule';
import Push from './Push';
import { useFirstRun } from './onboarding/FirstRun';

/**
 * The first run: configuration inside the wait, and then the chat. F-141, steps 2 and 3.
 *
 * Leo: *"nobody is going to wait 15 minutes for something to happen, they'll go to a different
 * tab or a different app, **or we ask them to do things in the meantime**… the onboarding should
 * take what it takes… **when they're done we know they're reachable**."*
 *
 * So this is not a loading screen and it is not a tour of the product. It is the moment the one
 * setting the product cannot work without gets made, placed at the only point in a learner's life
 * when they have nothing else to do and every reason to cooperate. Research is running behind it.
 *
 * **It takes what it takes.** Nothing here is paced against the research and nothing is padded to
 * fill it: a fast learner finishes and waits on the plan screen, a slow one is still going when
 * research ends, and neither is a problem. There is no progress bar, no step counter and no
 * estimate of how long this will take, because all three would be claims about a race nobody is
 * running.
 *
 * **Two steps and no framework.** The design says onboarding-as-a-system — resumable tours, a step
 * model, progress tracking — is explicitly not wanted: *"This is one flow, once, for a learner who
 * has just arrived."* So the step is derived from the same two facts the gate is derived from, and
 * reloading the page in the middle of it simply asks them again.
 *
 * ---
 *
 * **No tab bar, and it is worth stating rather than leaving to be discovered** (F-151 step 8).
 * `ObjectiveTabs` is rendered by the three screens that are about one plan — the chat, progress and
 * the plan — and this is not one of them; it is about the learner's reach window, which is global.
 * That is the whole of the guarantee, and it is structural: this file does not import the
 * component, and `FirstRunGate` redirects every guarded route except this one, so the bar cannot
 * appear while the gate is closed however many plans a learner has.
 *
 * It would also render nothing if it were here — `objectiveTabs` draws no bar below two plans, and
 * a gated learner has one at every tier anybody is on. **That is corroboration and not the
 * argument**, because it is a fact about the deployed population rather than about the code:
 * `ENTITLEMENTS` grants two at tier 2 and three at tier 3, and what makes it true today is only
 * that nothing writes `tier`. The sentence above does not depend on it.
 *
 * The screens below (`Schedule`, `Push`) carry no `?o=` for the same reason `sectionHref` refuses
 * to give them one: they act on settings that belong to the learner, not to a plan.
 */
export default function Welcome() {
  const { t } = useI18n();
  const { stage, note, mark } = useFirstRun();
  const navigate = useNavigate();

  /*
   * The mark is written when the gate is standing in front of somebody, not when they finish.
   *
   * It is what tells the chat, later, that this learner is owed the worked examples — and it has
   * to be set *here* because the only other evidence is the absence of a reach window, which is
   * the very thing the next press removes. A learner who already had a window never reaches this
   * effect — the gate never closes for them — and so is never marked, which is half of what keeps
   * the samples off their thread. The other half is that the mark is keyed by learner, because
   * this effect cannot know who was using the browser yesterday.
   */
  useEffect(() => {
    if (stage === 'configure') mark('configuring');
  }, [stage, mark]);

  // Not read yet. A redirect rendered against a reading that has not arrived sends somebody to
  // one screen and then moves them, which reads as the app deciding twice.
  if (stage === 'unknown') return <p className="muted">{t('welcome.deciding')}</p>;

  /*
   * Nothing owed: this address answers with a redirect for the rest of the account's life.
   *
   * That covers the returning learner stating a second objective — *"adding a new objective
   * doesn't do onboarding, that happens once"* — and everybody who was already using nabu when
   * this shipped, because both have a reach window and the gate never closes for either.
   */
  if (stage === 'open') return <Navigate to={ROUTES.plan} replace />;

  if (stage === 'tour') {
    return (
      <section className="panel">
        <h2>{t('welcome.done.title')}</h2>
        <p className="muted">{t('welcome.done.body')}</p>
        <div className="actions">
          {/*
           * To the chat, where the examples are rendered — rather than showing them here.
           *
           * The design is specific about this: *"Then the chat, pre-filled with worked
           * examples."* A briefing shown on a setup screen teaches the shape and not the place;
           * the point is that the learner recognises the thing when it turns up in the thread
           * they will be living in, so the illustration has to be in that thread.
           */}
          <button
            type="button"
            className="button primary"
            onClick={() => navigate(ROUTES.chat, { replace: true })}
          >
            {t('welcome.done.go')}
          </button>
        </div>
      </section>
    );
  }

  return (
    <>
      <section className="panel">
        <h2>{t('welcome.title')}</h2>
        <p className="muted">{t('welcome.lede')}</p>
        {/*
         * Why this is mandatory, said out loud, once.
         *
         * A gate whose reason is not given reads as an arbitrary obstacle, and the reason here is
         * genuinely good: this is the one setting without which nothing the product does gets
         * delivered. Saying it is also what stops the next reader softening the gate into a nag,
         * which is the thing it replaced.
         */}
        <p className="muted small">{t('welcome.why')}</p>
      </section>

      {/*
       * The reach window itself — the same component the configuration screen renders.
       *
       * One implementation, deliberately. A "guided" copy of these three controls would be a
       * second place the cap is validated, the spacing sentence is composed and the timezone
       * caveat is explained, and the two would disagree the first time one of them changed. What
       * this screen adds is the framing above and the consequence below; the controls are the
       * controls.
       */}
      {/*
       * **Told, not asked**, the same fix as the create above and for the same failure.
       *
       * `reread` was here, and `GET /api/window` reads with a plain `GetItem` — no `ConsistentRead`
       * (`packages/api/src/window.ts`). A re-read issued milliseconds after the write can
       * legitimately answer `configured: false`, which re-closes the gate under a learner looking
       * at the panel's own success message, with nothing polling to correct it: the only way out
       * was pressing Save again. `Schedule` already knows the truth — it has the stored window in
       * its hands — so the fact is handed over rather than re-derived.
       */}
      <Schedule onSaved={() => note({ windowConfigured: true })} />

      {/*
       * Notifications, offered and never required.
       *
       * They are not what the gate is about and must not be: on iOS there is no install API and
       * no way to prompt for push until the app has been added to the home screen, so a flow that
       * insisted on a granted permission would be unfinishable on the platform where it matters
       * most. Mail is the fallback F-122 built for exactly this, so a learner who declines here is
       * still reachable — which is why the gate is the window and this is a panel beside it.
       */}
      <Push />
    </>
  );
}
